/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { FiDownload, FiUsers, FiTrendingUp, FiDollarSign, FiAward, FiPieChart } from "react-icons/fi";
import PageContainer from "../../components/PageContainer";
import ResponsiveTable from "../../components/ResponsiveTable";
import UnifiedCard from "../../components/UnifiedCard";
import { useUI } from "../../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../../hooks/useTenantStorePermissions";
import NoAccess from "../../components/NoAccess";
import { formatNumber } from "utils/formatNumber";
import * as XLSX from "xlsx";
// Using colors from useUI instead of hardcoded COLORS

export default function CustomerReport() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);




  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [city, setCity] = useState("");
  const [minOrders, setMinOrders] = useState("");
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [activeTab, setActiveTab] = useState("buying"); // 'buying' or 'profitable'
  const [grandTotal, setGrandTotal] = useState(null);

  const tabs = [
    {
      id: "buying",
      label: isArabic ? "1- العملاء الأكثر شراءا" : "1- Most Buying Customers",
      icon: <FiTrendingUp />,
      sortKey: "ordersCount",
    },
    {
      id: "profitable",
      label: isArabic ? "2- العملاء الأكثر أرباحا" : "2- Most Profitable Customers",
      icon: <FiDollarSign />,
      sortKey: "totalOrdersValue",
    },
  ];

  // Fetch customer report from API
  useEffect(() => {
    const fetchReport = async () => {
      if (!perms.viewCustomerReport) return;
      if (!tenantId || tenantId === -1) return;

      setLoading(true);
      setHasFetched(false);

      try {
        const token = localStorage.getItem("authToken");

        // Select endpoint based on active tab (default to buying)
        let endpoint = `${baseUrl}/CustomerReports/customersbypurchase`;
        if (activeTab === "profitable") {
          endpoint = `${baseUrl}/CustomerReports/customersbyprofit`;
        }

        const params = new URLSearchParams();
        params.append("tenantId", tenantId.toString());
        params.append("top", "100"); // Fetch top 100 for the report

        if (city) params.append("city", city);
        if (minOrders) params.append("minOrders", minOrders);
        if (fromDate) params.append("fromDate", fromDate);
        if (toDate) params.append("toDate", toDate);

        const res = await fetch(`${endpoint}?${params.toString()}`, {
          method: "GET",
          headers: {
            Accept: "*/*",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });

        if (!res.ok) {
          setCustomerData([]);
          return;
        }

        const data = await res.json();

        if (!Array.isArray(data)) {
          setCustomerData([]);
          return;
        }

        const mappedData = data.map((c, index) => {
          const rawCustomerId = c.CustomerId ?? c.customerId ??
            (typeof c.customerId === "object" ? (c.customerId.customerId ?? c.customerId.id) : null);
          const safeCustomerId = rawCustomerId ?? index + 1;

          return {
            id: safeCustomerId,
            customerName: c.Name || c.customerName || c.name || "",
            customerId: String(safeCustomerId),
            ordersCount: Number(c.OrdersCount ?? c.ordersCount ?? 0),
            totalOrdersValue: Number(c.TotalPurchase ?? c.totalOrdersValue ?? 0),
            totalProfit: c.TotalProfit ?? c.totalProfit ?? null,
            city: c.City || c.city || "",
            firstOrderDate: c.FirstOrderDate || c.FirstOrder || c.firstOrderDate || "",
            lastOrderDate: c.LastOrderDate || c.LastOrder || c.lastOrderDate || "",
            totalProductsPurchased: Number(c.TotalProductsCount ?? c.TotalProducts ?? c.totalProductsPurchased ?? 0),
            returnedOrdersCount: Number(c.ReturnedOrdersCount ?? c.ReturnedOrders ?? c.returnedOrdersCount ?? 0),
            couponsUsedCount: Number(c.UsedCouponsCount ?? c.CouponsUsed ?? c.couponsUsedCount ?? 0),
          };
        });

        setCustomerData(mappedData);
      } catch (err) {
        console.error("Customer Report error:", err);
        setCustomerData([]);
      } finally {
        setLoading(false);
        setHasFetched(true);
      }
    };

    fetchReport();
  }, [
    activeTab,
    tenantId,
    baseUrl,
    perms.viewCustomerReport,
    city,
    minOrders,
    fromDate,
    toDate,
  ]);


  const filteredData = useMemo(() => {
    let sorted = [...customerData];
    if (activeTab === "buying") {
      sorted.sort((a, b) => (b.ordersCount || 0) - (a.ordersCount || 0));
    } else if (activeTab === "profitable") {
      sorted.sort((a, b) => (b.totalProfit || 0) - (a.totalProfit || 0));
    }
    return sorted;
  }, [customerData, activeTab]);

  const exportToExcel = () => {
    const dataToExport = filteredData.map(row => ({
      [isArabic ? "اسم العميل" : "Customer Name"]: row.customerName || "—",
      [isArabic ? "رقم العميل" : "Customer ID"]: row.customerId || "—",
      [isArabic ? "إجمالي الربح" : "Total Profit"]: row.totalProfit !== null ? row.totalProfit : "—",
      [isArabic ? "عدد الطلبات" : "Number of Orders"]: row.ordersCount || 0,
      [isArabic ? "قيمة الطلبات الكلي" : "Total Order Value"]: row.totalOrdersValue || 0,
      [isArabic ? "المدينة" : "City"]: row.city || "—",
      [isArabic ? "تاريخ اول طلب" : "Date of First Order"]: row.firstOrderDate || "—",
      [isArabic ? "تاريخ اخر طلب" : "Date of Last Order"]: row.lastOrderDate || "—",
      [isArabic ? "عدد المنتجات الكلي" : "Total Products"]: row.totalProductsPurchased || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customer Report");
    XLSX.writeFile(wb, `Customer_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (!perms.viewCustomerReport) {
    return <NoAccess />;
  }
  return (
    <PageContainer title={isArabic ? "تقرير العملاء" : "Customer Report"}>
      <h2 className="text-xl font-bold mb-4 px-2" style={{ color: colors.mainColor }}>
        {isArabic ? "العملاء" : "Customers"}
      </h2>
      {/* ===== Tabs / Cards ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {tabs.map((tab) => (
          <motion.div
            key={tab.id}
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveTab(activeTab === tab.id ? null : tab.id)}
            className="cursor-pointer"
          >
            <UnifiedCard
              className={`transition-all duration-300 ${activeTab === tab.id ? 'ring-2 ring-offset-2' : ''}`}
              style={{
                borderColor: activeTab === tab.id ? colors.mainColor : 'transparent',
                backgroundColor: activeTab === tab.id
                  ? (isDark ? 'rgba(116, 0, 191, 0.1)' : 'rgba(116, 0, 191, 0.05)')
                  : (isDark ? 'rgba(30, 41, 59, 0.5)' : '#fff'),
              }}
            >
              <div className="flex flex-col items-center justify-center py-4 text-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                  style={{
                    backgroundColor: activeTab === tab.id ? colors.mainColor : (isDark ? '#334155' : '#f1f5f9'),
                    color: activeTab === tab.id ? '#fff' : colors.mainColor
                  }}
                >
                  {tab.icon}
                </div>
                <h3 className={`text-lg font-bold ${activeTab === tab.id ? (isDark ? 'text-white' : colors.mainColor) : (isDark ? 'text-slate-200' : 'text-slate-700')}`}>
                  {tab.label}
                </h3>
              </div>
            </UnifiedCard>
          </motion.div>
        ))}
      </div>

      {/* ===== الفلاتر ===== */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-6 sm:mb-8"
      >
        <div className="flex flex-wrap items-end gap-3 sm:gap-4 px-2 sm:px-0" dir={isArabic ? "rtl" : "ltr"}>
          <div className="flex-1 min-w-[140px] sm:min-w-[180px]">
            <label className="block mb-2 text-sm font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827", textAlign: isArabic ? "right" : "left" }}>
              {isArabic ? "من تاريخ" : "From Date"}
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm transition-all"
              dir={isArabic ? "rtl" : "ltr"}
              style={{
                backgroundColor: isDark ? "#1E2533" : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}`,
                color: isDark ? "#fff" : "#111827",
                textAlign: isArabic ? "right" : "left",
              }}
            />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block mb-2 text-sm font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827", textAlign: isArabic ? "right" : "left" }}>
              {isArabic ? "إلى تاريخ" : "To Date"}
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-4 py-2 rounded-lg text-sm transition-all"
              dir={isArabic ? "rtl" : "ltr"}
              style={{
                backgroundColor: isDark ? "#1E2533" : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}`,
                color: isDark ? "#fff" : "#111827",
                textAlign: isArabic ? "right" : "left",
              }}
            />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block mb-2 text-sm font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827", textAlign: isArabic ? "right" : "left" }}>
              {isArabic ? "المدينة" : "City"}
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder={isArabic ? "أدخل المدينة" : "Enter city"}
              className="w-full px-4 py-2 rounded-lg text-sm transition-all"
              dir={isArabic ? "rtl" : "ltr"}
              style={{
                backgroundColor: isDark ? "#1E2533" : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}`,
                color: isDark ? "#fff" : "#111827",
                textAlign: isArabic ? "right" : "left",
              }}
            />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block mb-2 text-sm font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827", textAlign: isArabic ? "right" : "left" }}>
              {isArabic ? "الحد الأدنى للطلبات" : "Min Orders"}
            </label>
            <input
              type="number"
              value={minOrders}
              onChange={(e) => setMinOrders(e.target.value)}
              placeholder={isArabic ? "أدخل الحد الأدنى للطلبات" : "Enter minimum"}
              className="w-full px-4 py-2 rounded-lg text-sm transition-all"
              dir={isArabic ? "rtl" : "ltr"}
              style={{
                backgroundColor: isDark ? "#1E2533" : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}`,
                color: isDark ? "#fff" : "#111827",
                textAlign: isArabic ? "right" : "left",
              }}
            />
          </div>
        </div>
      </motion.div>

      {/* ===== جدول العملاء ===== */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: colors.mainColor }}></div>
            <p className="mt-4" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
              {isArabic ? "جاري تحميل بيانات العملاء..." : "Loading customer data..."}
            </p>
          </div>
        ) : hasFetched && filteredData.length === 0 ? (
          <div className="text-center py-12">
            <p>{isArabic ? "لا توجد بيانات عملاء متاحة" : "No customer data available"}</p>
          </div>
        )
          : (
            <ResponsiveTable
              columns={[
                { header: isArabic ? "اسم العميل" : "Customer Name", sortKey: "customerName" },
                { header: isArabic ? "رقم العميل" : "Customer ID", sortKey: "customerId" },
                ...(activeTab === "profitable" ? [{ header: isArabic ? "إجمالي الربح" : "Total Profit", sortKey: "totalProfit" }] : []),
                { header: isArabic ? "عدد الطلبات" : "Number of Orders", sortKey: "ordersCount" },
                { header: isArabic ? "قيمة الشراء الكلي" : "Total Purchase", sortKey: "totalOrdersValue" },
                { header: isArabic ? "المدينة" : "City", sortKey: "city" },
                { header: isArabic ? "تاريخ اول طلب" : "Date of First Order", sortKey: "firstOrderDate" },
                { header: isArabic ? "تاريخ اخر طلب" : "Date of Last Order", sortKey: "lastOrderDate" },
                { header: isArabic ? "عدد المنتجات الكلي" : "Total Products Purchased", sortKey: "totalProductsPurchased" },
                { header: isArabic ? "عدد الطلبات المرجعة" : "Returned Orders", sortKey: "returnedOrdersCount" },
                { header: isArabic ? "الكوبونات المستخدمة" : "Coupons Used", sortKey: "couponsUsedCount" },
              ]}
              data={filteredData}
              searchable={true}
              sortable={true}
              searchFields={["customerName", "customerId", "city"]}
              headerActions={
                <button
                  onClick={exportToExcel}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-white transition-all shadow-md"
                  style={{ backgroundColor: colors.mainColor }}
                >
                  <FiDownload size={18} /> {isArabic ? "تنزيل إكسل" : "Excel Download"}
                </button>
              }
              renderRow={(row) => (
                <>
                  <td className="py-3 px-4 text-center font-medium" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    {row.customerName || "—"}
                  </td>
                  <td className="py-3 px-4 text-center" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                    {row.customerId || "—"}
                  </td>
                  {activeTab === "profitable" && (
                    <td className="py-3 px-4 text-center font-bold" style={{ color: colors.gold }}>
                      {row.totalProfit !== null ? formatNumber(Math.round(row.totalProfit)) : "—"} {row.totalProfit !== null && (isArabic ? "د.ع" : "IRQ")}
                    </td>
                  )}
                  <td className="py-3 px-4 text-center font-semibold" style={{ color: colors.mainColor }}>
                    {row.ordersCount || 0}
                  </td>
                  <td className="py-3 px-4 text-center font-semibold" style={{ color: colors.green }}>
                    {formatNumber(Math.round(row.totalOrdersValue || 0))} {isArabic ? "د.ع" : "IRQ"}
                  </td>
                  <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    {row.city || "—"}
                  </td>
                  <td className="py-3 px-4 text-center" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                    {row.firstOrderDate ? new Date(row.firstOrderDate).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-3 px-4 text-center" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                    {row.lastOrderDate ? new Date(row.lastOrderDate).toLocaleDateString() : "—"}                </td>
                  <td className="py-3 px-4 text-center font-semibold" style={{ color: colors.blue }}>
                    {row.totalProductsPurchased || 0}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span
                      className="px-2 py-1 rounded text-xs font-semibold"
                      style={{
                        backgroundColor: (row.returnedOrdersCount || 0) > 0 ? "rgba(239, 68, 68, 0.1)" : "rgba(34, 197, 94, 0.1)",
                        color: (row.returnedOrdersCount || 0) > 0 ? colors.red : colors.green,
                      }}
                    >
                      {row.returnedOrdersCount || 0}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center font-semibold" style={{ color: colors.gold }}>
                    {row.couponsUsedCount || 0}
                  </td>
                </>
              )}
            />
          )}
      </motion.div>
    </PageContainer>
  );
}

